import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { invoiceService } from '../services/invoiceService';

/**
 * Emite una factura con retry SEGURO ante 503 ("Session storage temporarily
 * unavailable"). Ese 503 ocurre al leer la sesión en el backend, ANTES de
 * llamar a Aludra → la factura NO se emitió. Reintentamos reusando la MISMA
 * idempotency-key, de modo que aunque el backend hubiera avanzado, deduplica y
 * NUNCA se emite dos veces. Solo se reintenta el 503, no otros errores.
 */
async function emitInvoiceWithRetry(payload: any) {
  const idempotencyKey = crypto.randomUUID();
  const MAX_RETRIES = 2;
  for (let attempt = 0; ; attempt++) {
    try {
      return await invoiceService.createInvoice(payload, idempotencyKey);
    } catch (err) {
      const status = (err as AxiosError | undefined)?.response?.status;
      if (status === 503 && attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1))); // 1s, 2s
        continue;
      }
      throw err;
    }
  }
}

export function useInvoices(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  initDate?: string;
  endDate?: string;
  companyId?: string;
}) {
  console.log('🔍 useInvoices hook called with params:', params);

  const companyId = params?.companyId;

  return useQuery({
    queryKey: ['invoices', companyId, params], // Include companyId in the key
    queryFn: () => {
      console.log('🔍 useInvoices queryFn executing for company:', companyId);
      console.log('📤 Sending params to API:', params);
      return invoiceService.getInvoices(params); // Send ALL params including companyId
    },
    enabled: !!companyId, // Only fetch if we have a company selected
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
  });
}

export function useInvoicesArchive(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  initDate?: string;
  endDate?: string;
  storeCode?: string;
  billingPoint?: string;
  electronicInvoiceId?: string;
  cufe?: string;
  customerName?: string;
  documentNumber?: string;
  customerNumberId?: string;
  invoiceStatus?: string;
  documentType?: string;
  companyId?: string;
}) {
  const companyId = params?.companyId;

  return useQuery({
    queryKey: ['invoices', 'archive', companyId, params], // Include companyId in the key
    queryFn: () => {
      console.log('🔍 useInvoicesArchive queryFn executing for company:', companyId);
      console.log('📤 Sending params to API:', params);
      return invoiceService.getInvoicesArchive(params); // Send ALL params including companyId
    },
    enabled: !!companyId, // Only fetch if we have a company selected
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoiceService.getInvoiceById(id),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => emitInvoiceWithRetry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useResendToDGI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => invoiceService.resendToDGI(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invoiceService.deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}
